import * as THREE from "three";
import { Button } from "./button";
import { Object3D, Texture, Vector3 } from "three";
import { LollipopHelper } from "./lollipop-helper";
import { AssetManager } from "./AssetManager";
import { addDepthOverride } from "./material-utils";


export enum KioskContent {
  None,
  W25_HotSpot_1,
  W25_HotSpot_2,
  W25_HotSpot_3,
  W31_HotSpot_3,
  W25_HotSpot_4,
  W31_HotSpot_4,
  W25_HotSpot_5,
  W25_HotSpot_6,
  W31_HotSpot_6,
  W25_HotSpot_7,
  W25_HotSpot_7_extension,
  W25_HotSpot_7_ammonia,
  W25_HotSpot_7_diesel,
  W25_HotSpot_7_df,
  W25_HotSpot_8,
  W31_HotSpot_8,
  W25_EmergencyStop,

  W31_HotSpot_11,
  W31_HotSpot_12_diesel,
  W31_HotSpot_12_df,
  W31_HotSpot_13,
  W31_HotSpot_14,

  // there's no content for this hotspot, it only plays an animation
  // W31_HotSpot_Anim_Covers,

}


// a floating screen akin to VR main menu with different contents
export class HotspotKiosk extends THREE.Group {
  private _headLevelY: number = 0;
  private _uiDistance: number = 0.45;
  private _uiScale: number = 0.5;
  private _uiSideOffset: number = 0.35;

  // private _graphicMaterial: THREE.Material = new THREE.MeshBasicMaterial({color: 0x00ff00});
  // private _graphicMesh = new THREE.Mesh(new THREE.PlaneGeometry(), this._graphicMaterial);

  // private _lookatValue = 0;

  // private _lookatBaseDelay = 15;
  // private _lookatSensitivity = 0.01;

  private _content = new Map<KioskContent, THREE.Mesh>();

  private _leftContainer = new THREE.Group();
  private _leftExtension = new THREE.Group();
  private _rightContainer = new THREE.Group();

  // private _leftContainer = new LollipopHelper();
  // private _rightContainer = new LollipopHelper();

  private _currentContent: KioskContent = KioskContent.None;

  private _mainModel?: THREE.Object3D;

  set mainModel(mainModel: THREE.Object3D) {
    this._mainModel = mainModel;
  }

  get currentContent(): KioskContent {
    return this._currentContent;
  }

  constructor() {
    super();

    // this._graphicMaterial = graphicMaterial ?? this._graphicMaterial;
    // this._graphicMesh.material = this._graphicMaterial;
    // this.add(this._graphicMesh);
    // this.add(new LollipopHelper())

    this.add(this._leftContainer, this._rightContainer);
    this._leftContainer.add(this._leftExtension);
    this._leftExtension.rotateOnWorldAxis(
      new THREE.Vector3(0, 1, 0),
      0.33 * Math.PI
    );

    this.moveExtensionForward(0.51);
    this.moveExtensionRight(-1);

    this.renderOrder = 2000;
  }

  /**
   * Move the left extension group along its local forward vector (negative Z in local space)
   * @param distance - Distance to move (positive = forward, negative = backward)
   */
  moveExtensionForward(distance: number): void {
    const localForward = new THREE.Vector3(0, 0, -1);
    localForward.applyQuaternion(this._leftExtension.quaternion);
    this._leftExtension.position.add(localForward.multiplyScalar(distance));
    console.log(`Extension moved forward by ${distance.toFixed(3)}, new position:`, this._leftExtension.position);
  }

  /**
   * Move the left extension group along its local right vector (positive X in local space)
   * @param distance - Distance to move (positive = right, negative = left)
   */
  moveExtensionRight(distance: number): void {
    const localRight = new THREE.Vector3(1, 0, 0);
    localRight.applyQuaternion(this._leftExtension.quaternion);
    this._leftExtension.position.add(localRight.multiplyScalar(distance));
    console.log(`Extension moved right by ${distance.toFixed(3)}, new position:`, this._leftExtension.position);
  }

  /**
   * Move the left extension group along its local up vector (positive Y in local space)
   * @param distance - Distance to move (positive = up, negative = down)
   */
  moveExtensionUp(distance: number): void {
    const localUp = new THREE.Vector3(0, 1, 0);
    localUp.applyQuaternion(this._leftExtension.quaternion);
    this._leftExtension.position.add(localUp.multiplyScalar(distance));
    console.log(`Extension moved up by ${distance.toFixed(3)}, new position:`, this._leftExtension.position);
  }

  /**
   * Set the left extension position using local orientation offsets
   * @param forward - Distance along local forward vector (positive = forward)
   * @param right - Distance along local right vector (positive = right)
   * @param up - Distance along local up vector (positive = up)
   */
  setExtensionOffset(forward: number, right: number, up: number = 0): void {
    // Reset position first
    this._leftExtension.position.set(0, 0, 0);
    
    // Calculate local vectors based on current rotation
    const localForward = new THREE.Vector3(0, 0, -1).applyQuaternion(this._leftExtension.quaternion);
    const localRight = new THREE.Vector3(1, 0, 0).applyQuaternion(this._leftExtension.quaternion);
    const localUp = new THREE.Vector3(0, 1, 0).applyQuaternion(this._leftExtension.quaternion);
    
    // Apply offsets
    const offset = new THREE.Vector3();
    offset.add(localForward.multiplyScalar(forward));
    offset.add(localRight.multiplyScalar(right));
    offset.add(localUp.multiplyScalar(up));
    
    this._leftExtension.position.copy(offset);
    console.log(`Extension offset set to: forward=${forward}, right=${right}, up=${up}`);
    console.log(`New position:`, this._leftExtension.position);
  }

  /**
   * Get current extension position
   */
  getExtensionPosition(): THREE.Vector3 {
    return this._leftExtension.position.clone();
  }

  setupContent(assetManager: AssetManager) {
    const fallbackMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
    const contentMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.Material()
    );
    contentMesh.material = fallbackMaterial;

    const hotspot1 = contentMesh.clone();
    hotspot1.material =
      assetManager.getMaterial("hotspot_1") ?? fallbackMaterial;
    this._content.set(KioskContent.W25_HotSpot_1, hotspot1);

    const hotspot2 = contentMesh.clone();
    hotspot2.material =
      assetManager.getMaterial("hotspot_2") ?? fallbackMaterial;
    this._content.set(KioskContent.W25_HotSpot_2, hotspot2);

    const hotspot3 = contentMesh.clone();
    hotspot3.material =
      assetManager.getMaterial("hotspot_3") ?? fallbackMaterial;
    this._content.set(KioskContent.W25_HotSpot_3, hotspot3);

    const hotspot4 = contentMesh.clone();
    hotspot4.material =
      assetManager.getMaterial("hotspot_4") ?? fallbackMaterial;
    this._content.set(KioskContent.W25_HotSpot_4, hotspot4);

    const hotspot5 = contentMesh.clone();
    hotspot5.material =
      assetManager.getMaterial("hotspot_5") ?? fallbackMaterial;
    this._content.set(KioskContent.W25_HotSpot_5, hotspot5);

    const hotspot6 = contentMesh.clone();
    hotspot6.material =
      assetManager.getMaterial("hotspot_6") ?? fallbackMaterial;
    this._content.set(KioskContent.W25_HotSpot_6, hotspot6);

    const hotspot7 = contentMesh.clone();
    hotspot7.material =
      assetManager.getMaterial("hotspot_7") ?? fallbackMaterial;
    this._content.set(KioskContent.W25_HotSpot_7, hotspot7);

    const hotspot7_extension = contentMesh.clone();
    hotspot7_extension.material =
      assetManager.getMaterial("hotspot_7_2") ?? fallbackMaterial;
    this._content.set(KioskContent.W25_HotSpot_7_extension, hotspot7_extension);

    const hotspot7_ammonia = contentMesh.clone();
    hotspot7_ammonia.material =
      assetManager.getMaterial("hotspot_7_ammonia") ?? fallbackMaterial;
    this._content.set(KioskContent.W25_HotSpot_7_ammonia, hotspot7_ammonia);

    const hotspot7_diesel = contentMesh.clone();
    hotspot7_diesel.material =
      assetManager.getMaterial("hotspot_7_diesel") ?? fallbackMaterial;
    this._content.set(KioskContent.W25_HotSpot_7_diesel, hotspot7_diesel);

    const hotspot7_df = contentMesh.clone();
    hotspot7_df.material =
      assetManager.getMaterial("hotspot_7_df") ?? fallbackMaterial;
    this._content.set(KioskContent.W25_HotSpot_7_df, hotspot7_df);

    const hotspot8 = contentMesh.clone();
    hotspot8.material =
      assetManager.getMaterial("hotspot_8") ?? fallbackMaterial;
    this._content.set(KioskContent.W25_HotSpot_8, hotspot8);

    const emergencyStop = contentMesh.clone();
    emergencyStop.material =
      assetManager.getMaterial("hotspot_emergency_stop") ?? fallbackMaterial;
    this._content.set(KioskContent.W25_EmergencyStop, emergencyStop);

    // W31 hotspot contents
    const hotspot3_W31 = contentMesh.clone();
    hotspot3_W31.material =
      assetManager.getMaterial("hotspot_3_W31") ?? fallbackMaterial;
    this._content.set(KioskContent.W31_HotSpot_3, hotspot3_W31);

    const hotspot4_W31 = contentMesh.clone();
    hotspot4_W31.material =
      assetManager.getMaterial("hotspot_4_W31") ?? fallbackMaterial;
    this._content.set(KioskContent.W31_HotSpot_4, hotspot4_W31);

    const hotspot6_W31 = contentMesh.clone();
    hotspot6_W31.material =
      assetManager.getMaterial("hotspot_6_W31") ?? fallbackMaterial;
    this._content.set(KioskContent.W31_HotSpot_6, hotspot6_W31);

    const hotspot8_W31 = contentMesh.clone();
    hotspot8_W31.material =
      assetManager.getMaterial("hotspot_8_W31") ?? fallbackMaterial;
    this._content.set(KioskContent.W31_HotSpot_8, hotspot8_W31);

    const hotspot11_W31 = contentMesh.clone();
    hotspot11_W31.material =
      assetManager.getMaterial("hotspot_11_W31") ?? fallbackMaterial;
    this._content.set(KioskContent.W31_HotSpot_11, hotspot11_W31);

    const hotspot12_W31_diesel = contentMesh.clone();
    hotspot12_W31_diesel.material =
      assetManager.getMaterial("hotspot_12_diesel_W31") ?? fallbackMaterial;
    this._content.set(KioskContent.W31_HotSpot_12_diesel, hotspot12_W31_diesel);

    const hotspot12_W31_df = contentMesh.clone();
    hotspot12_W31_df.material =
      assetManager.getMaterial("hotspot_12_df_W31") ?? fallbackMaterial;
    this._content.set(KioskContent.W31_HotSpot_12_df, hotspot12_W31_df);

    const hotspot13_W31 = contentMesh.clone();
    hotspot13_W31.material =
      assetManager.getMaterial("hotspot_13_W31") ?? fallbackMaterial;
    this._content.set(KioskContent.W31_HotSpot_13, hotspot13_W31);

    const hotspot14_W31 = contentMesh.clone();
    hotspot14_W31.material =
      assetManager.getMaterial("hotspot_14_W31") ?? fallbackMaterial;
    this._content.set(KioskContent.W31_HotSpot_14, hotspot14_W31);

    // const hotspotAnimCovers_W31 = contentMesh.clone();
    // hotspotAnimCovers_W31.material =
    //   assetManager.getMaterial("hotspot_anim_covers_W31") ?? fallbackMaterial;
    // this._content.set(KioskContent.W31_HotSpot_Anim_Covers, hotspotAnimCovers_W31);



    this._content.forEach((content, key) => {
      if (
        key === KioskContent.W25_HotSpot_7_ammonia ||
        key === KioskContent.W25_HotSpot_7_diesel ||
        key === KioskContent.W25_HotSpot_7_df
      ) {
        this._rightContainer.add(content);
      } else if (key === KioskContent.W25_HotSpot_7_extension) {
        this._leftExtension.add(content);
      } else {
        this._leftContainer.add(content);
      }
      if (content.material instanceof THREE.Material) {
        // content.material.depthWrite = false;
        addDepthOverride(content.material, 0.0);
        content.visible = false;
      }
    });

    // const content = await assetManager.loadGLBModel("hotspot-kiosk");
    // this.add(content);
  }

  showKioskContent(
    xrCameras: {
      leftCamera?: THREE.PerspectiveCamera;
      midPoint: THREE.Vector3;
      keepPosition?: boolean;
    },
    content: KioskContent,
    keepPosition = false
  ): void {
    // use a static position for some contents
    // didn't work out straight away, so disabled for now
    // if (content === KioskContent.HotSpot7_ammonia || content === KioskContent.HotSpot7_diesel || content === KioskContent.HotSpot7_df || content === KioskContent.HotSpot7) {
    //   // this._rightContainer.parent = this._floorRoot;
    //   this._rightContainer.position.copy(this._floorRoot.position);
    //   this._rightContainer.position.set(0, xrCameras.midPoint.y, 0);
    //   this._rightContainer.rotation.copy(this._floorRoot.rotation);
    //   this._rightContainer.add(new THREE.AxesHelper(1));

    // } else {
    // }

    // Store the head level Y position (only set once when first detected)
    if (!this._headLevelY) {
      this._headLevelY = xrCameras.midPoint.y;
      console.log(`Head level Y set to: ${this._headLevelY.toFixed(2)}`);
    }
    console.log("showingcontent", content);
    if (
      content === KioskContent.W25_HotSpot_7_ammonia ||
      content === KioskContent.W25_HotSpot_7_diesel ||
      content === KioskContent.W25_HotSpot_7_df ||
      content === KioskContent.W25_HotSpot_7
    ) {
      this.updateKioskPositionWorld();
    } else if (!keepPosition) {
      this.updateKioskPosition(
        xrCameras,
        content === KioskContent.W25_EmergencyStop
      );
    }

    for (const content of this._content.values()) {
      content.visible = false;
    }
    const contentMatch = this._content.get(content);
    if (contentMatch) {
      contentMatch.visible = true;
      this._currentContent = content;

      if (
        contentMatch.material instanceof THREE.MeshBasicMaterial &&
        contentMatch.material.map !== null
      ) {
        const aspect =
          contentMatch.material.map.image.width /
          contentMatch.material.map.image.height;
        contentMatch.scale.set(
          this._uiScale * aspect,
          this._uiScale,
          this._uiScale
        );
      } else {
        contentMatch.scale.set(this._uiScale, this._uiScale, this._uiScale);
      }

      if (
        content === KioskContent.W25_HotSpot_7_ammonia ||
        content === KioskContent.W25_HotSpot_7_diesel ||
        content === KioskContent.W25_HotSpot_7_df
      ) {
        const spot7 = this._content.get(KioskContent.W25_HotSpot_7);
        const spot7_extension = this._content.get(
          KioskContent.W25_HotSpot_7_extension
        );
        if (spot7 && spot7_extension) {
          const scale7 = 2;
          spot7.visible = true;
          spot7_extension.visible = true;
          if (
            spot7.material instanceof THREE.MeshBasicMaterial &&
            spot7.material.map !== null
          ) {
            const aspect =
              spot7.material.map.image.width / spot7.material.map.image.height;
            spot7.scale.set(
              this._uiScale * aspect * scale7,
              this._uiScale * scale7,
              this._uiScale * scale7
            );
            // spot7.scale.multiplyScalar(1.75);
            // this._leftContainer.position.y = this._headLevelY + 0.25
          } else {
            spot7.scale.set(
              this._uiScale * scale7,
              this._uiScale * scale7,
              this._uiScale * scale7
            );
            // spot7.scale.multiplyScalar(2);
          }

          if (
            spot7_extension.material instanceof THREE.MeshBasicMaterial &&
            spot7_extension.material.map !== null
          ) {
            const aspect =
              spot7_extension.material.map.image.width /
              spot7_extension.material.map.image.height;
            spot7_extension.scale.set(
              this._uiScale * aspect * scale7,
              this._uiScale * scale7,
              this._uiScale * scale7
            );
            // spot7.scale.multiplyScalar(1.75);
            // this._leftContainer.position.y = this._headLevelY + 0.25
          } else {
            spot7_extension.scale.set(
              this._uiScale * scale7,
              this._uiScale * scale7,
              this._uiScale * scale7
            );
            // spot7.scale.multiplyScalar(2);
          }
        }
      }
    } else {
      console.error(`Content ${content} not found`);
      console.error("content keys", this._content.keys());
      this._currentContent = KioskContent.None;
    }
  }

  update(deltatime: number): void {
    // this._graphicMesh.rotation.y += deltatime * 10;
    // TODO: change the lookedAt value depending on how directly the user is looking at the content
    // this._graphicMesh.rotateOnWorldAxis(new THREE.Vector3(0, 1, 0), Math.PI * performance.now() / 1000);
  }

  /**
   * Update 3D UI positioning when XR session is active
   */
  updateKioskPosition(
    xrCameras: {
      leftCamera?: THREE.PerspectiveCamera;
      midPoint: THREE.Vector3;
    },
    middle: boolean = false
  ): void {

    this.add(this._leftContainer, this._rightContainer);

    // Get camera world position and rotation
    const cameraQuaternion = new THREE.Quaternion();
    const cameraScale = new THREE.Vector3();

    // this is probably unecessary to be done by decomposing the camera
    xrCameras.leftCamera?.matrixWorld.decompose(
      new Vector3(),
      cameraQuaternion,
      cameraScale
    );

    // Create a horizontal forward vector (ignore pitch/roll, only use yaw)
    const horizontalForward = new THREE.Vector3(0, 0, -1);
    // Extract only the Y-axis rotation from the camera quaternion
    const yawQuaternion = new THREE.Quaternion().setFromEuler(
      new THREE.Euler(
        0,
        Math.atan2(
          2 *
            (cameraQuaternion.w * cameraQuaternion.y +
              cameraQuaternion.x * cameraQuaternion.z),
          1 -
            2 *
              (cameraQuaternion.y * cameraQuaternion.y +
                cameraQuaternion.z * cameraQuaternion.z)
        ),
        0
      )
    );
    horizontalForward.applyQuaternion(yawQuaternion);

    // Create a left vector perpendicular to the forward vector
    const horizontalLeft = new THREE.Vector3(-1, 0, 0);
    horizontalLeft.applyQuaternion(yawQuaternion);
    const horizontalRight = new THREE.Vector3(1, 0, 0);
    horizontalRight.applyQuaternion(yawQuaternion);

    // Set UI position at head level with horizontal tracking and left offset
    this._leftContainer.position.set(
      xrCameras.midPoint.x +
        horizontalForward.x * this._uiDistance +
        horizontalLeft.x * this._uiSideOffset * (middle ? 0 : 1),
      this._headLevelY, // Keep at fixed head level
      xrCameras.midPoint.z +
        horizontalForward.z * this._uiDistance +
        horizontalLeft.z * this._uiSideOffset * (middle ? 0 : 1)
    );

    this._rightContainer.position.set(
      xrCameras.midPoint.x +
        horizontalForward.x * this._uiDistance +
        horizontalRight.x * this._uiSideOffset * 1.75,
      this._headLevelY, // Keep at fixed head level
      xrCameras.midPoint.z +
        horizontalForward.z * this._uiDistance +
        horizontalRight.z * this._uiSideOffset
    );

    // Make UI face the user horizontally (only look at X,Z position, not Y)
    const lookAtPosition = new THREE.Vector3(
      xrCameras.midPoint.x,
      this._headLevelY,
      xrCameras.midPoint.z
    );
    this._leftContainer.lookAt(lookAtPosition);
    this._rightContainer.lookAt(lookAtPosition);
  }

  // set the kiosk statically in the world
  // use for UNIC control panel scenario so the controls don't get obscured
  updateKioskPositionWorld() {
    if (this._mainModel !== undefined) {
      
      this._mainModel.add(this._leftContainer, this._rightContainer);
      this._leftContainer.position.set(0, this._headLevelY, 0.85);
      this._rightContainer.position.set(0, this._headLevelY, -0.65);
      this._leftContainer.rotation.set(0, Math.PI * 0.75, 0);
      this._rightContainer.rotation.set(0, Math.PI * 0.25, 0);

      // this.add(this._leftContainer, this._rightContainer);


      // this._leftContainer.lookAt(new THREE.Vector3(0, 0, 0));
      // this._rightContainer.lookAt(new THREE.Vector3(0, 0, 0));

    }
  }
}
